import {
  AfterViewInit,
  Directive,
  ElementRef,
  OnDestroy,
  Renderer2,
} from '@angular/core';

// This directive targets the goab-badge component to remove aria-expanded and related attributes from its internal icon element,
// which is not interactive and should not be announced as expandable by screen readers. It uses a MutationObserver to ensure that
// any dynamic changes to the badge's content do not reintroduce these attributes.
// most likely, the aria-expanded tag is cloned from DDL control by mistake in GoA UI component library.
// Usage: Add the badgeAccessibility directive to any goab-badge element to enhance its accessibility by preventing screen readers from misinterpreting it as an expandable element.
@Directive({
  selector: '[badgeAccessibility]',
  standalone: true,
})
export class BadgeAccessibilityDirective implements AfterViewInit, OnDestroy {
  private observer!: MutationObserver;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      const host = this.el.nativeElement.querySelector('goa-badge')?.shadowRoot;
      if (!host) {
        return;
      }

      const badgeIcon = host.querySelector('div > goa-icon')?.shadowRoot;
      if (!badgeIcon) {
        return;
      }

      const badgeEL = badgeIcon.querySelector('div') as HTMLElement;
      if (!badgeEL) {
        return;
      }

      this.fixAttribute(badgeEL);

      this.observer = new MutationObserver(() => {
        this.fixAttribute(badgeEL);
      });

      this.observer.observe(badgeEL, {
        childList: true,
        attributes: true,
        subtree: true,
        attributeFilter: [
          'aria-expanded',
          'aria-controls',
          'aria-label',
          'content',
        ],
      });
    }, 0);
  }

  private fixAttribute(root: HTMLElement): void {
    const targets = new Set<HTMLElement>([
      root,
      ...Array.from(
        root.querySelectorAll<HTMLElement>('[aria-expanded], [aria-controls]')
      ),
    ]);

    targets.forEach((element) => {
      this.renderer.removeAttribute(element, 'aria-expanded');
      this.renderer.removeAttribute(element, 'aria-controls');
    });
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}
